/**
 * Activity page (STAFF_ACCESS_PLAN Phase 4) — the venue "who did what" feed.
 * Every wired action (bookings, check-ins, slots, staff changes…) shows up
 * here under the name of whoever did it. Gated by activity.view in the nav;
 * the backend enforces the capability on the endpoint too.
 */
import { useCallback, useEffect, useState } from 'react';
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import { vendorApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import {
  ActivityEntry, ACTION_TYPES, actionIcon, actionChipClass, formatWhen,
} from '../utils/activityHelpers';

interface Pagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export default function ActivityNew() {
  const { user } = useAuth();
  const { isOwner } = useVenueRole();
  const venueId = user?.venues?.[0]?.id as number | undefined;

  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [page, setPage] = useState(1);
  const [actionType, setActionType] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [staffOptions, setStaffOptions] = useState<{ id: number; name: string }[]>([]);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await vendorApi.getVenueActivity(venueId, {
        page,
        action_type: actionType || undefined,
        user_id: staffFilter ? Number(staffFilter) : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
      setEntries(res.data);
      setPagination(res.pagination);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, actionType, staffFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  // Staff dropdown is an owner nicety — the staff list endpoint is owner-only,
  // so staff viewers just don't get the person filter.
  useEffect(() => {
    if (!venueId || !isOwner) return;
    vendorApi.getStaff(venueId)
      .then(data => setStaffOptions(
        (data.staff as { user_id: number; name: string }[]).map(s => ({ id: s.user_id, name: s.name }))
      ))
      .catch(() => setStaffOptions([]));
  }, [venueId, isOwner]);

  const resetToFirstPage = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const setType = resetToFirstPage(setActionType);
  const setStaff = resetToFirstPage(setStaffFilter);
  const setFrom = resetToFirstPage(setFromDate);
  const setTo = resetToFirstPage(setToDate);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5" /> Activity
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Who did what at your venue — bookings, check-ins, slots and team changes.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select value={actionType} onChange={e => setType(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All actions</option>
          {Object.entries(ACTION_TYPES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {isOwner && staffOptions.length > 0 && (
          <select value={staffFilter} onChange={e => setStaff(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Everyone</option>
            {user?.id && <option value={String(user.id)}>Me</option>}
            {staffOptions.map(s => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        )}

        <input type="date" value={fromDate} onChange={e => setFrom(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" aria-label="From date" />
        <input type="date" value={toDate} onChange={e => setTo(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" aria-label="To date" />

        {(actionType || staffFilter || fromDate || toDate) && (
          <button
            onClick={() => { setActionType(''); setStaffFilter(''); setFromDate(''); setToDate(''); setPage(1); }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-2">
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{error}</div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 text-sm">Loading activity…</div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">
            No activity yet{(actionType || staffFilter || fromDate || toDate) ? ' for these filters' : ''}.
            Actions taken from this dashboard will appear here.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {entries.map(entry => {
              const Icon = actionIcon(entry.action_type);
              return (
                <li key={entry.id} className="p-4 md:px-6 flex items-start gap-3">
                  <div className={`mt-0.5 p-2 rounded-lg shrink-0 ${actionChipClass(entry.action_type)}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{entry.user?.name ?? 'Unknown'}</span>
                      {' — '}
                      <span>{entry.description}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${actionChipClass(entry.action_type)}`}>
                        {ACTION_TYPES[entry.action_type] ?? entry.action_type}
                      </span>
                      <span className="text-xs text-gray-400">{formatWhen(entry.created_at)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">
            Page {pagination.current_page} of {pagination.last_page} · {pagination.total} events
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={pagination.current_page <= 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={pagination.current_page >= pagination.last_page}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
